import TomSelect from 'tom-select/dist/js/tom-select.base.js'

// Submit the form via unpoly, but add some more query params
function submitFormWithParams(form: HTMLFormElement, newParams: object) {
  const renderOptions = up.form.submitOptions(form)
  let paramEntries = renderOptions.params.toArray()

  if (renderOptions.target) {
    renderOptions.target = [renderOptions.target, '.career-layout--filters'].join(',')
  }

  for (let [newName, newValues] of Object.entries(newParams)) {
    if (up.util.isArray(newValues)) {
      newName = newName + '[]'
    } else {
      newValues = [newValues]
    }
    paramEntries = paramEntries.filter(({ name }) => name !== newName)
    for (const newValue of newValues) {
      paramEntries.push({ name: newName, value: newValue })
    }
  }
  up.render({ ...renderOptions, params: new up.Params(paramEntries) })
}

// Autocomplete for the query field.
// The problem here is that we want to allow the user to completely ignore the autocomplete
// and not force them to select an option or anything. Tomselect does not support this out of the box.
// What we do is:
// - Have a regular text field for the query.
// - Add a hidden field directly beneath, and run Tomselect on that hidden field.
// - Listen to events on the text field, and
//   - manually update the tomselect
//   - forward some events (like Arrow keys etc) to tomselect
// - When the user selects an option, immediately trigger a search.
up.compiler(
  '[data-career-search-query-select]',
  (
    element: HTMLInputElement,
    { path, allowEmptyQuery = false }: { path: string; allowEmptyQuery: boolean },
  ) => {
    const form = element.closest('form')!

    // add the hidden field to position the dropdown
    const proxyInput = document.createElement('input')
    proxyInput.type = 'hidden'
    proxyInput.classList.add('career-search-form--hidden-autocomplete')
    element.parentElement!.insertBefore(proxyInput, element.nextSibling)

    const tomselect = new TomSelect(proxyInput, {
      valueField: 'search_params',
      labelField: 'label',
      searchField: 'label',
      hideSelected: false,
      sortField: [{ field: '$order' }, { field: '$score' }],
      optgroupField: 'group',
      optgroupValueField: 'group',
      optgroups: [
        { group: 'vocation', label: 'Berufe' },
        { group: 'company', label: 'Unternehmen' },
      ],
      async load(query: string, callback: (response: object) => void) {
        const url = new URL(path, window.location.origin)
        const searchParams = new URLSearchParams(url.searchParams)
        searchParams.set('term', query)
        url.search = searchParams.toString()

        const response = await (await fetch(url)).json()
        this.clearOptions()
        callback(response)
      },
      onChange(value: string) {
        tomselect.clear(true)
        tomselect.clearOptions()
        tomselect.setTextboxValue('')
        tomselect.close()
        element.value = ''
        submitFormWithParams(form, JSON.parse(value))
      },
      render: {
        // we simply do not want the dropdown to show at all when it's still loading, or when we found nothing
        loading() {},
        no_results() {},
      },
    })

    // we have to track if the user tried to interact with the dropdown via keyboard events
    let interactedWithDropdown = false

    // patch the "refreshOptions" method to avoid tomselect automatically focusing the first available option
    const originalRefreshOptions = tomselect.refreshOptions
    tomselect.refreshOptions = (...args) => {
      originalRefreshOptions.apply(tomselect, args)
      if (!interactedWithDropdown) {
        tomselect.clearActiveOption()
      }
    }

    function refreshOptions() {
      if (allowEmptyQuery || element.value.length >= 2) {
        tomselect.load(element.value)
      } else {
        tomselect.clearOptions()
        tomselect.close()
      }
    }

    element.addEventListener('input', () => {
      tomselect.setTextboxValue(element.value)
      refreshOptions()
    })

    tomselect.on('load', (data: object[]) => {
      if (data.length) {
        tomselect.open()
      }
    })

    element.addEventListener('keydown', (event) => {
      const forwardedKeys = ['ArrowDown', 'ArrowUp', 'Escape', 'Enter']
      if (forwardedKeys.includes(event.key)) {
        if (interactedWithDropdown) {
          tomselect.onKeyDown(event)
        } else {
          // first interaction. If this is the arrow down key, focus the first option manually.
          interactedWithDropdown = true
          const selectable = tomselect.selectable()
          if (event.key === 'ArrowDown' && selectable.length) {
            // we need to select the first element manually
            tomselect.setActiveOption(selectable[0])
          } else {
            tomselect.onKeyDown(event)
          }
        }
      }
    })

    element.addEventListener('focus', (event) => {
      tomselect.onFocus(event)
      if (!(tomselect.options as any).length) {
        refreshOptions()
      }
    })

    element.addEventListener('blur', (event) => {
      tomselect.onBlur(event)
    })

    return () => {
      tomselect.destroy()
    }
  },
)
